import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Circle, Lock, ChevronDown, ChevronUp, Eye, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

export interface JourneyPlan {
  planId: string;
  title: string;
  status: string;
  daysCompleted: number;
  totalDays: number;
  completionDate?: string;
  order: number;
}

interface JourneyTimelineProps {
  plans: JourneyPlan[];
  currentPlanId?: string;
  className?: string;
  /** Called when user wants to revisit a completed plan in the coach */
  onRevisit?: (planId: string) => void;
  /** Called when user wants to view a specific lesson from a completed plan */
  onViewLesson?: (planId: string, dayNumber: number) => void;
  /** Day lesson titles for the active/completed plans — keyed by planId then dayNumber */
  allPlanDayTitles?: Record<string, Record<number, string>>;
}

export const JourneyTimeline: React.FC<JourneyTimelineProps> = ({
  plans,
  currentPlanId,
  className = '',
  onRevisit,
  onViewLesson,
  allPlanDayTitles,
}) => {
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);

  if (!plans || plans.length === 0) return null;

  const sorted = [...plans].sort((a, b) => a.order - b.order);

  return (
    <motion.div
      initial={{ opacity: 0, x: 12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, delay: 0.12 }}
      className={className}
    >
      <Card className="rounded-2xl border-2 overflow-hidden shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-foreground">Your Journey</CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-emerald-500/40 via-primary/30 to-muted" />

            <div className="space-y-3">
              {sorted.map((plan, idx) => {
                const isCompleted = plan.status === 'COMPLETED';
                const isCurrent = plan.planId === currentPlanId || plan.status === 'IN_PROGRESS';
                const isLocked = !isCompleted && !isCurrent;
                const isExpanded = expandedPlanId === plan.planId;
                const dayTitles = allPlanDayTitles?.[plan.planId] ?? {};

                return (
                  <motion.div
                    key={plan.planId}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.08 }}
                    className="relative pl-0"
                  >
                    <div className="flex items-start gap-3">
                      {/* Node */}
                      <div className="relative z-10 flex-shrink-0 mt-0.5">
                        {isCompleted ? (
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/15">
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          </div>
                        ) : isCurrent ? (
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 ring-2 ring-primary/30 ring-offset-1 ring-offset-background">
                            <Circle className="h-3 w-3 fill-primary text-primary" />
                          </div>
                        ) : (
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted">
                            <Lock className="h-3 w-3 text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className={`text-sm font-medium leading-tight truncate flex-1 ${
                            isLocked ? 'text-muted-foreground' : 'text-foreground'
                          }`}>
                            {plan.title || `Plan ${plan.order}`}
                          </p>
                          {isCompleted && (
                            <button
                              type="button"
                              onClick={() => setExpandedPlanId(isExpanded ? null : plan.planId)}
                              className="shrink-0 p-0.5 text-muted-foreground hover:text-primary transition-colors"
                            >
                              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-xs ${
                            isCompleted ? 'text-emerald-600 dark:text-emerald-400'
                              : isCurrent ? 'text-primary'
                              : 'text-muted-foreground'
                          }`}>
                            {isCompleted ? (
                              <>
                                {plan.daysCompleted}/{plan.totalDays} days
                                {plan.completionDate && (
                                  <> &middot; {new Date(plan.completionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</>
                                )}
                              </>
                            ) : isCurrent ? (
                              <>{plan.daysCompleted}/{plan.totalDays} days &middot; in progress</>
                            ) : (
                              <>Upcoming</>
                            )}
                          </span>
                          {isCompleted && onRevisit && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onRevisit(plan.planId); }}
                              className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/20 transition-colors"
                            >
                              <MessageSquare className="h-3 w-3" />
                              Coach
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded day list for completed plans */}
                    <AnimatePresence>
                      {isCompleted && isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden ml-9 mt-2"
                        >
                          <div className="space-y-1.5 border-l-2 border-emerald-500/20 pl-3">
                            {Array.from({ length: plan.totalDays }, (_, i) => i + 1).map((dayNum) => {
                              const dayTitle = dayTitles[dayNum] || `Day ${dayNum}`;
                              return (
                                <div key={dayNum} className="flex items-center gap-2 group">
                                  <CheckCircle2 className="h-3 w-3 text-emerald-500/60 shrink-0" />
                                  <span className="text-xs text-foreground/70 truncate flex-1">
                                    {dayTitle}
                                  </span>
                                  {onViewLesson && (
                                    <button
                                      type="button"
                                      onClick={() => onViewLesson(plan.planId, dayNum)}
                                      className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-0.5 rounded bg-muted/80 px-1.5 py-0.5 text-[10px] text-foreground/60 hover:text-primary hover:bg-primary/10"
                                    >
                                      <Eye className="h-2.5 w-2.5" />
                                      View
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
